import { getProgressSteps } from "./charli-progress.js";

export function hasDisplayableContent(message) {
  if (!message?.parts) return false;
  return message.parts.some(part => part.type === "text" && String(part.text || "").trim());
}

export function shouldShowResults(phase, message) {
  return phase === "completed" && hasDisplayableContent(message);
}

export function getRowFingerprint(message, phase = "ready") {
  return JSON.stringify({
    id: message?.id || null,
    role: message?.role || "assistant",
    parts: message?.parts || [],
    phase,
  });
}

export function createDesiredRows(messages, lifecycle) {
  const rows = (messages || [])
    .filter(message => message.role !== "tool" && message.id !== lifecycle?.supersededAssistantMessageId)
    .map(message => {
      const ownsLifecycle = message.id === lifecycle?.assistantMessageId;
      const phase = ownsLifecycle
        ? lifecycle.phase
        : (message.metadata?.charliOutcome || (message.role === "assistant" ? "completed" : "ready"));
      const waiting = ownsLifecycle && !lifecycle.terminal && !hasDisplayableContent(message);
      return {
        key: message.id,
        message,
        role: message.role,
        requestId: ownsLifecycle ? lifecycle.requestId : null,
        pending: false,
        phase,
        progressSteps: waiting ? getProgressSteps(message) : [],
        fingerprint: waiting
          ? `waiting:${lifecycle.requestId}:${JSON.stringify(getProgressSteps(message))}`
          : getRowFingerprint(message, phase),
      };
    });

  const needsPending = lifecycle && !lifecycle.assistantMessageId;

  if (needsPending) {
    const key = `pending:${lifecycle.requestId}`;
    rows.push({
      key,
      message: null,
      role: "assistant",
      requestId: lifecycle.requestId,
      pending: true,
      phase: lifecycle.phase,
      progressSteps: [],
      fingerprint: `waiting:${lifecycle.requestId}:[]`,
    });
  }

  return rows;
}

export function planRowReconciliation(desiredRows, existingRows) {
  const existingByKey = new Map((existingRows || []).map(row => [row.key, row]));
  const desiredKeys = new Set(desiredRows.map(row => row.key));
  const operations = [];

  desiredRows.forEach((row, index) => {
    const existing = existingByKey.get(row.key);
    if (existing) {
      if (existing.fingerprint !== row.fingerprint) {
        operations.push({ type: "update", key: row.key, index, row });
      }
      if (existing.index !== index) {
        operations.push({ type: "move", key: row.key, index, row });
      }
      return;
    }

    const adoptable = (existingRows || []).find(candidate =>
      row.requestId &&
      candidate.requestId === row.requestId &&
      !desiredKeys.has(candidate.key)
    );

    if (adoptable) {
      operations.push({ type: "adopt", key: row.key, fromKey: adoptable.key, index, row });
      return;
    }

    operations.push({ type: "insert", key: row.key, index, row });
  });

  (existingRows || []).forEach(row => {
    if (!desiredKeys.has(row.key) && !operations.some(op => op.type === "adopt" && op.fromKey === row.key)) {
      operations.push({ type: "remove", key: row.key });
    }
  });

  return operations;
}

export function getResultKey(result, type, index = 0) {
  const objectId = String(result?.objectID || "").trim();
  if (objectId) return `${type}:${objectId}`;
  const identity = type === "supplier"
    ? `${result?.title || ""}|${result?.link || ""}`
    : `${result?.name || ""}|${result?.link || ""}`;
  return `${type}:fallback:${identity}:${index}`;
}

export function getResultSectionConfig(type) {
  return type === "product"
    ? { className: "chamfr-ai-results--products", label: "Products" }
    : { className: "chamfr-ai-results--suppliers", label: "Suppliers" };
}

export function shouldAnimateResultCards(phase, requestId) {
  return phase === "completed" && Boolean(requestId);
}

// Cards fade in together; a stagger kept invisible cards holding space in an empty bubble.
export function getResultCardReveal(index, enabled, prefersReducedMotion) {
  if (!enabled || prefersReducedMotion) return null;
  return { delay: 0 };
}

export function planResultCardReconciliation(results, existingKeys, type) {
  const desired = (results || []).map((result, index) => ({
    key: getResultKey(result, type, index),
    result,
    index,
  }));
  const desiredKeys = new Set(desired.map(item => item.key));
  const currentKeys = new Set(existingKeys || []);

  return {
    desired,
    insert: desired.filter(item => !currentKeys.has(item.key)),
    remove: (existingKeys || []).filter(key => !desiredKeys.has(key)),
  };
}

// Phone streaming: show link labels instead of raw markdown, and hold back a link
// whose markup has not finished arriving so the visible text only ever grows.
export function toStreamingDisplayText(text) {
  let display = String(text || "").replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, "$1");
  const open = display.lastIndexOf("[");
  if (open >= 0 && !display.slice(open).includes("\n")) display = display.slice(0, open);
  return display;
}

// Next reveal point on a word boundary: at least two words per tick, more when behind.
export function nextRevealLength(shownLength, target) {
  const words = target.slice(shownLength).match(/\s*\S+/g);
  if (!words) return target.length;
  const step = Math.max(2, Math.ceil(words.length / 12));
  return shownLength + words.slice(0, step).join("").length;
}
